import { DateTimePicker } from '@mui/lab';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import UserSearch from '../../../shared-components/UserSearch';
import { ICategory } from '../../../shared-interfaces/Notation/ICategory';
import { IReason } from '../../../shared-interfaces/Notation/IReason';
import { ICoreUser } from '../../../shared-interfaces/User/ICoreUser';
import UpdateNoteService from '../../../shared-services/Notation/UpdateNoteService';
import UserService from '../../../shared-services/User/UserService';
import { Role } from '../../../shared-types/Roles';
import BeaconRte, {
  convertBeaconRteToHtml,
  EditorValueHasText,
  EmptyRteValue,
} from '../../BeaconRte';
import InstitutionContext from '../../contexts/InstitutionContext';
import NotationAttributeContext from '../../contexts/NotationAttributesContext';
import PrivacyPolicy from '../../../shared-components/PrivacyPolicy';
import ErrorBox from '../../ErrorBox';
import OutlinedFormControl from '../../OutlinedFormControl';
import CreateNotation from './CreateNotation';

export interface IUpdateNotationForm {
  reset: (isSuccess?: boolean) => void;
  isPreview?: boolean;
  updateAddAudiencePermission: boolean;
  updateAudiencePermission: boolean;
  updateCategoryPermission: boolean;
  updateOccurancePermission: boolean;
  updateReasonPermission: boolean;
  updateShowPermission: boolean;
}

const UpdateNotationForm = (props: IUpdateNotationForm) => {
  const institution = useContext(InstitutionContext);
  const notationContext = useContext(NotationAttributeContext);
  const { categories, reasons } = notationContext;

  const [rteValue, setRteValue] = useState(EmptyRteValue);
  const [students, setStudents] = useState<ICoreUser[]>([]);
  const [isStudentAllowedToView, setIsStudentAllowedToView] =
    useState<boolean>(false);
  const [audience, setAudience] = useState<string>(
    props.updateAudiencePermission ? '' : 'All',
  );
  const [occurrenceDate, setOccurrenceDate] = useState<Date | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<ICategory[]>([]);
  const [selectedReasons, setSelectedReasons] = useState<IReason[]>([]);
  const [additionalAudience, setAdditionalAudience] = useState<ICoreUser[]>([]);
  const [studentNetwork, setStudentNetwork] = useState<ICoreUser[]>([]);
  const [selectedNetworkMembers, setSelectedNetworkMembers] = useState<
    ICoreUser[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState<Boolean>(false);
  const [errorOpen, setErrorOpen] = useState<boolean>(false);
  const [overFifty, setOverFifty] = useState(false);

  useEffect(() => {
    if (students && students.length === 1) {
      UserService.getStudentNetwork(students[0].id).then(x =>
        setStudentNetwork(x),
      );
      setSelectedNetworkMembers([]);
    } else if (students?.length > 1) {
      if (audience === 'SpecificNetworkMembers') setAudience('');
      setStudentNetwork([]);
      setSelectedNetworkMembers([]);
    }
  }, [students]);

  useEffect(() => {
    if (isSubmitting) {
      ReactGA.event({
        category: 'NotationWidget',
        action: 'submit',
        label: JSON.stringify({
          insitutionId: institution.institution.id,
          notationType: 'update',
        }),
        nonInteraction: false,
      });
    }
  }, [isSubmitting]);

  // const selectedStyle = { backgroundColor: '#06c', color: 'white' };
  const isMultiStudent = students ? students?.length > 1 : false;

  return (
    <>
      <CreateNotation name="Update" reset={props.reset}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <FormControl style={{ width: '100%' }}>
              <UserSearch
                label="Name or External Id of Student(s) (Max 10)"
                onSelect={x => setStudents(x)}
                roleRestriction={[Role.Student, Role.PeerAdvisor]}
                value={students}
                maxSearchLength={10}
                onSearchLengthError={setOverFifty}
                multiple
              />
              <FormHelperText>Required</FormHelperText>
              {overFifty && (
                <Typography color="error">
                  Updates can only be created for up to 10 students at a time.
                  Only the first 10 entered will be used.
                </Typography>
              )}
            </FormControl>
          </Grid>
          {props.updateAudiencePermission && (
            <Grid item>
              <OutlinedFormControl
                label="Audience"
                style={{ width: '100%' }}
                required
              >
                <RadioGroup
                  aria-label="Audience"
                  name="audience"
                  value={audience}
                  onChange={v => setAudience(v.target.value)}
                >
                  <FormControlLabel
                    value="Private"
                    control={<Radio />}
                    label="Private"
                  />
                  <FormControlLabel
                    value="All"
                    control={<Radio />}
                    label="Only professional staff"
                  />
                  <FormControlLabel
                    value="FullNetwork"
                    control={<Radio />}
                    label="Professional staff + peer advisors"
                  />
                  <Tooltip
                    title={
                      isMultiStudent
                        ? 'Specific Staff Members can only be selected for one student'
                        : 'Please select a student to see their network'
                    }
                    arrow
                    placement="top"
                    disableHoverListener={studentNetwork.length > 0}
                  >
                    <span>
                      <FormControlLabel
                        value="SpecificNetworkMembers"
                        control={<Radio />}
                        label="Specific Staff Members"
                        disabled={studentNetwork.length === 0}
                      />
                    </span>
                  </Tooltip>
                </RadioGroup>

                {audience === 'SpecificNetworkMembers' && (
                  <FormControl variant="outlined" style={{ width: '100%' }}>
                    <InputLabel id="network-label">
                      Specific Network Members
                    </InputLabel>
                    <Select
                      labelId="network-label"
                      id="network"
                      value={selectedNetworkMembers.map(x => x.id)}
                      label="Specific Network Members"
                      multiple
                      renderValue={selected => {
                        if (selected.length > 1)
                          return `${selected.length} network members selected`;
                        return (selected as string[])
                          .map(s => {
                            const user = selectedNetworkMembers.find(
                              x => x.id === s,
                            );
                            return `${
                              user?.preferredFirstName ?? user?.firstName
                            } ${user?.lastName}`;
                          })
                          .join(', ');
                      }}
                      onChange={e => {
                        setSelectedNetworkMembers(
                          studentNetwork.filter(
                            x =>
                              (e.target.value as string[]).findIndex(
                                y => x.id === y,
                              ) !== -1,
                          ),
                        );
                      }}
                    >
                      {studentNetwork.map(x => (
                        <MenuItem key={x.id} value={x.id}>
                          <ListItemIcon>
                            <Checkbox
                              checked={selectedNetworkMembers.indexOf(x) !== -1}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${x.preferredFirstName ?? x.firstName} ${
                              x.lastName
                            }`}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </OutlinedFormControl>
            </Grid>
          )}

          {institution.settings.sendStudentsNotifications && (
            <Grid item>
              <FormControlLabel
                style={{ paddingLeft: '8px' }}
                control={
                  <Checkbox
                    checked={isStudentAllowedToView}
                    onChange={() =>
                      setIsStudentAllowedToView(!isStudentAllowedToView)
                    }
                    inputProps={{
                      'aria-label': 'Allow student to view alert',
                    }}
                  />
                }
                label="Allow student to view update"
                aria-label="Allow student to view update"
              />
            </Grid>
          )}

          {props.updateAddAudiencePermission && (
            <Grid item>
              <UserSearch
                label="Additional Audience"
                multiple
                disabled={students === undefined}
                onSelect={x => setAdditionalAudience(x)}
                roleRestriction={[
                  Role.PeerAdvisor,
                  Role.Advisor,
                  Role.Generalist,
                  Role.Executive,
                  Role.Administrator,
                ]}
                // assignableTo={student?.id}
              />
            </Grid>
          )}
          {props.updateOccurancePermission && (
            <Grid item>
              <DateTimePicker
                maxDateTime={new Date()}
                clearable
                label="Occurrence Date"
                value={occurrenceDate}
                onChange={date => setOccurrenceDate(date)}
                renderInput={props => <TextField fullWidth {...props} />}
              />
            </Grid>
          )}
          {props.updateCategoryPermission && (
            <Grid item>
              <FormControl variant="outlined" style={{ width: '100%' }}>
                <InputLabel id="categories-label">Categories</InputLabel>
                <Select
                  labelId="categories-label"
                  id="categories"
                  value={selectedCategories.map(x => x.id)}
                  label="Categories"
                  multiple
                  renderValue={selected => {
                    if (selected.length > 1)
                      return `${selected.length} categories selected`;
                    return (selected as number[])
                      .map(s => selectedCategories.find(x => x.id === s)?.name)
                      .join(', ');
                  }}
                  onChange={e => {
                    setSelectedCategories(
                      categories.filter(
                        x =>
                          (e.target.value as number[]).findIndex(
                            y => x.id === y,
                          ) !== -1,
                      ),
                    );
                  }}
                  // onClose={() => {       }}
                >
                  {categories.map(x => (
                    <MenuItem key={x.id} value={x.id}>
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedCategories.indexOf(x) !== -1}
                        />
                      </ListItemIcon>
                      <ListItemText primary={x.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {props.updateReasonPermission && (
            <Grid item>
              <FormControl
                variant="outlined"
                style={{
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <InputLabel id="reasons-label">Reasons</InputLabel>
                <Select
                  labelId="reasons-label"
                  id="reasons"
                  value={selectedReasons.map(x => x.id)}
                  label="Reasons"
                  multiple
                  renderValue={selected => {
                    if (selected.length > 1)
                      return `${selected.length} reasons selected`;
                    return (selected as number[])
                      .map(s => selectedReasons.find(x => x.id === s)?.name)
                      .join(', ');
                  }}
                  onChange={e => {
                    setSelectedReasons(
                      reasons.filter(
                        x =>
                          (e.target.value as number[]).findIndex(
                            y => x.id === y,
                          ) !== -1,
                      ),
                    );
                  }}
                  // onClose={() => {       }}
                >
                  {reasons.map(x => (
                    <MenuItem key={x.id} value={x.id}>
                      <ListItemIcon>
                        <Checkbox checked={selectedReasons.indexOf(x) !== -1} />
                      </ListItemIcon>
                      <ListItemText primary={x.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item>
            <BeaconRte
              required
              label="Description"
              value={rteValue}
              onChange={setRteValue}
            />
        </Grid>
        <Grid item>
            <PrivacyPolicy policyHtml={notationContext.assessmentFerpaStatement} />
        </Grid>
          <Grid item>
            <div style={{ float: 'right' }}>
              <Button
                variant="text"
                color="secondary"
                onClick={() => props.reset()}
                aria-label="Cancel"
              >
                Cancel
              </Button>
              <Button
                aria-label="Create Update"
                variant="contained"
                color="primary"
                style={{ marginRight: '1em' }}
                onClick={() => {
                  if (props.isPreview) return;
                  setIsSubmitting(true);
                  // submit
                  UpdateNoteService.createMultiple({
                    audienceType: audience,
                    additionalAudienceMembers: additionalAudience.map(
                      x => x.id,
                    ),
                    audienceMembers: selectedNetworkMembers.map(x => x.id),
                    details: convertBeaconRteToHtml(rteValue),
                    studentIds: students?.map(x => x.id),
                    selectedCategoryIds: selectedCategories.map(x => x.id),
                    selectedReasonIds: selectedReasons.map(x => x.id),
                    occurenceDate: occurrenceDate,
                    canStudentView: isStudentAllowedToView,
                  }, false)
                    .then(() => props.reset(true))
                    .catch(() => {
                      setIsSubmitting(false);
                      setErrorOpen(true);
                    });
                }}
                disabled={
                  isSubmitting ||
                  !students ||
                  students.length === 0 ||
                  !audience ||
                  (rteValue && !EditorValueHasText(rteValue)) ||
                  (audience === 'SpecificNetworkMembers' &&
                    selectedNetworkMembers.length === 0)
                }
              >
                Create Update
              </Button>
            </div>
          </Grid>
        </Grid>
        <Dialog onClose={() => setErrorOpen(false)} open={errorOpen}>
          <ErrorBox message="Failed to Create Update(s)" showCorporateContact />
          <DialogActions>
            <Button aria-label="Close" onClick={() => setErrorOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </CreateNotation>
      {isSubmitting && <LinearProgress />}
    </>
  );
};

export default UpdateNotationForm;
